import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Loader from '../../components/Loader/Loader';
import api from '../../api/index.js';
import s from './Characteristics.module.scss';

const SUPPLIER_LABELS = {
  api_equip: 'Equip',
  api_td: 'TD',
  api_bio: 'Bio',
  api_klen: 'Klen',
  api_practika: 'Practika',
  api_foodatlas: 'Foodatlas',
  api_rusholod: 'Rusholod',
};

const supplierLabel = (api) => SUPPLIER_LABELS[api] || api;
const rowKey = (row) => `${row.api_name}::${row.source_name}`;
const llmJobStorageKey = (catId) => `characteristics-llm-job:${catId}`;

const formatElapsed = (startedAt, nowMs = Date.now()) => {
  if (!startedAt) return '';
  const sec = Math.max(0, Math.floor(nowMs / 1000 - Number(startedAt)));
  const m = Math.floor(sec / 60);
  if (m < 1) return `${sec} сек`;
  if (m < 60) return `${m} мин`;
  return `${Math.floor(m / 60)} ч ${m % 60} мин`;
};
const bxJobStorageKey = (catId) => `characteristics-bx-job:${catId}`;
const recomputeJobStorageKey = (catId) => `characteristics-recompute-job:${catId}`;
const UNIT_LABELS = { mm: 'мм', cm: 'см', kg: 'кг', kW: 'кВт', V: 'В' };
const formatUnit = (unit) => (unit ? (UNIT_LABELS[unit] || unit) : '—');
const formatRawValue = (value) => {
  const text = String(value ?? '');
  if (text.length > 60) return `${text.slice(0, 60)}…`;
  return text;
};

const EQUIP_FIELD_TITLES = {
  widthmm: 'Ширина',
  lengthmm: 'Длина',
  heightmm: 'Высота',
  grosswidthmm: 'Ширина брутто',
  grosslengthmm: 'Длина брутто',
  grossheightmm: 'Высота брутто',
  weightkg: 'Вес нетто',
  grossweightkg: 'Вес брутто',
  powerkvt: 'Мощность',
  voltage: 'Напряжение',
  country: 'Страна',
  warranty: 'Гарантия',
};

const resolveSourceTitle = (apiName, sourceName, sourceTitleRu) => {
  if (sourceTitleRu) return sourceTitleRu;
  if (apiName === 'api_equip' && sourceName) {
    return EQUIP_FIELD_TITLES[String(sourceName).toLowerCase()] || '';
  }
  return '';
};

const formatSupplierField = (apiName, sourceName, sourceTitleRu) => {
  const raw = (sourceName || '').trim();
  const ru = resolveSourceTitle(apiName, sourceName, sourceTitleRu).trim();
  if (ru && ru.toLowerCase() !== raw.toLowerCase()) {
    return `${ru} (${raw})`;
  }
  return raw;
};

const formatSourceLine = (src) => (
  `${supplierLabel(src.api_name)} · ${formatSupplierField(src.api_name, src.source_name, src.source_title_ru)} = ${formatRawValue(src.raw_value)} (${src.count})`
);

const DATA_VIEWS = new Set(['properties', 'values']);

const CategoryCharacteristics = () => {
  const { cat_id } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [fields, setFields] = useState([]);
  const [mappedFields, setMappedFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeProgress, setRecomputeProgress] = useState(null);
  const [recomputeStartedAt, setRecomputeStartedAt] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [llmMapping, setLlmMapping] = useState(false);
  const [llmStartedAt, setLlmStartedAt] = useState(null);
  const [llmClock, setLlmClock] = useState(0);
  const [bxExporting, setBxExporting] = useState(false);
  const [bxProgress, setBxProgress] = useState(null);
  const [bxResult, setBxResult] = useState(null);
  const [needsRecompute, setNeedsRecompute] = useState(false);
  const [view, setView] = useState('unmapped');
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [valuesData, setValuesData] = useState(null);
  const [valuesLoading, setValuesLoading] = useState(false);
  const [valuesSearch, setValuesSearch] = useState('');
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [mergeRow, setMergeRow] = useState(null);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeSelected, setMergeSelected] = useState('');
  const [flashKey, setFlashKey] = useState(null);
  const [msgType, setMsgType] = useState('info');
  const llmPollRef = useRef(null);
  const bxPollRef = useRef(null);
  const recomputePollRef = useRef(null);

  const stopLlmPoll = () => {
    if (llmPollRef.current) {
      window.clearInterval(llmPollRef.current);
      llmPollRef.current = null;
    }
  };

  const load = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);

    const sum = await api.characteristics.getCategorySummary(cat_id);
    if (!sum.error) setSummary(sum);

    const allRaw = await api.characteristics.getCategoryRawFields(cat_id, {
      relevant_only: false,
      only_unmapped: false,
    });

    if (!allRaw.error) {
      const items = allRaw.items || [];
      setFields(items.filter((f) => !f.mapping));
      setMappedFields(items.filter((f) => f.mapping));
    }
    if (!opts.silent) setLoading(false);
  }, [cat_id]);

  const handleBxFlagChange = async (prop, field, value) => {
    if (!prop.definition_id) return;
    setProperties((prev) => prev.map((p) => (
      p.code === prop.code ? { ...p, [field]: value, ...(field === 'bx_show_in_filter' ? { bx_filter_manual: true } : {}) } : p
    )));
    const res = await api.characteristics.patchDefinition(prop.definition_id, { [field]: value });
    if (res.error) {
      showToast(res.error, 'error');
      loadProperties();
    }
  };

  const loadProperties = useCallback(async () => {
    setPropertiesLoading(true);
    const res = await api.characteristics.getCategoryProperties(cat_id);
    if (!res.error) setProperties(res.items || []);
    setPropertiesLoading(false);
  }, [cat_id]);

  const loadValues = useCallback(async () => {
    setValuesLoading(true);
    const res = await api.characteristics.getCategoryValues(cat_id, {
      preview: true,
      include_raw: false,
      include_products: false,
    });
    if (!res.error) setValuesData(res);
    setValuesLoading(false);
  }, [cat_id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (view === 'properties' || view === 'values') loadProperties();
  }, [view, loadProperties]);

  useEffect(() => {
    if (view === 'values') loadValues();
  }, [view, loadValues]);

  const showToast = (text, type = 'info') => {
    setMsg(text);
    setMsgType(type);
  };

  const finishLlmJob = useCallback((result, errorText) => {
    stopLlmPoll();
    sessionStorage.removeItem(llmJobStorageKey(cat_id));
    setLlmMapping(false);
    setLlmStartedAt(null);

    if (errorText) {
      showToast(errorText, 'error');
      return;
    }

    setNeedsRecompute(true);
    const clusters = result?.clusters_applied ?? 0;
    const mappings = result?.mappings_applied ?? result?.applied ?? 0;
    const reviewCount = (result?.review || []).filter((r) => r.status === 'review').length;
    showToast(
      `AI: ${clusters} свойств, ${mappings} привязок${reviewCount ? `, на проверку ${reviewCount}` : ''}. Нажмите «Применить к товарам».`,
      'success',
    );
    load({ silent: true });
    loadProperties();
    loadValues();
  }, [cat_id, load, loadProperties, loadValues]);

  const pollLlmJob = useCallback((jobId) => {
    stopLlmPoll();
    setLlmMapping(true);

    const tick = async () => {
      const status = await api.characteristics.getLlmAutoMapStatus(cat_id, { job_id: jobId });
      if (status.error) {
        finishLlmJob(null, status.error);
        return;
      }
      if (status.status === 'running') {
        if (status.started_at) setLlmStartedAt(status.started_at);
        return;
      }
      if (status.status === 'done') {
        finishLlmJob(status.result);
        return;
      }
      if (status.status === 'error') {
        finishLlmJob(null, status.error || 'AI завершился с ошибкой');
        return;
      }
      if (status.status === 'idle') {
        finishLlmJob(null, 'Задача AI не найдена — возможно, сервер перезапускали');
      }
    };

    tick();
    llmPollRef.current = window.setInterval(tick, 2500);
  }, [cat_id, finishLlmJob]);

  useEffect(() => () => stopLlmPoll(), []);

  useEffect(() => {
    if (!llmMapping && !recomputing) return undefined;
    setLlmClock(Date.now());
    const id = window.setInterval(() => setLlmClock(Date.now()), 15000);
    return () => window.clearInterval(id);
  }, [llmMapping, recomputing]);

  useEffect(() => {
    const resume = async () => {
      const saved = sessionStorage.getItem(llmJobStorageKey(cat_id));
      const savedJobId = saved ? JSON.parse(saved).job_id : null;
      const status = await api.characteristics.getLlmAutoMapStatus(
        cat_id,
        savedJobId ? { job_id: savedJobId } : {},
      );
      if (status.error) return;
      if (status.status === 'running') {
        if (status.job_id) {
          sessionStorage.setItem(
            llmJobStorageKey(cat_id),
            JSON.stringify({ job_id: status.job_id }),
          );
        }
        showToast('AI продолжает настройку полей…', 'info');
        pollLlmJob(status.job_id || savedJobId);
      } else if (status.status === 'done' && status.result) {
        sessionStorage.removeItem(llmJobStorageKey(cat_id));
        finishLlmJob(status.result);
      } else if (savedJobId && status.status === 'idle') {
        sessionStorage.removeItem(llmJobStorageKey(cat_id));
      }
    };
    resume();
  }, [cat_id, pollLlmJob, finishLlmJob]);

  const mergeChoices = useMemo(() => {
    const codes = new Map();
    mappedFields.forEach((f) => {
      if (f.mapping?.attribute_code && f.mapping.attribute_title) {
        codes.set(f.mapping.attribute_code, f.mapping.attribute_title);
      }
    });
    return [...codes.entries()]
      .map(([code, title]) => ({ code, title }))
      .sort((a, b) => a.title.localeCompare(b.title, 'ru'));
  }, [mappedFields]);

  const listFields = view === 'mapped' ? mappedFields : fields;

  const primaryMappingKeys = useMemo(() => {
    const earliest = new Map();

    const isBetterPrimary = (next, prev) => {
      if (next.created != null && prev.created != null) return next.created < prev.created;
      if (next.created != null) return true;
      if (prev.created != null) return false;
      if (next.nameMatch !== prev.nameMatch) return next.nameMatch < prev.nameMatch;
      return next.key < prev.key;
    };

    mappedFields.forEach((row) => {
      const code = row.mapping?.attribute_code;
      if (!code) return;
      const key = rowKey(row);
      const candidate = {
        key,
        created: row.mapping?.created_at ?? null,
        nameMatch: row.source_name === row.mapping?.attribute_title ? 0 : 1,
      };
      const prev = earliest.get(code);
      if (!prev || isBetterPrimary(candidate, prev)) {
        earliest.set(code, candidate);
      }
    });
    return new Set([...earliest.values()].map((item) => item.key));
  }, [mappedFields]);

  const filteredFields = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = listFields.filter((row) => {
      if (supplierFilter && row.api_name !== supplierFilter) return false;
      if (!q) return true;
      const title = row.mapping?.attribute_title || '';
      return (
        row.source_name.toLowerCase().includes(q)
        || resolveSourceTitle(row.api_name, row.source_name, row.source_title_ru).toLowerCase().includes(q)
        || supplierLabel(row.api_name).toLowerCase().includes(q)
        || title.toLowerCase().includes(q)
      );
    });

    if (view !== 'mapped') return rows;

    return [...rows].sort((a, b) => {
      const titleA = a.mapping?.attribute_title || '';
      const titleB = b.mapping?.attribute_title || '';
      const byTitle = titleA.localeCompare(titleB, 'ru');
      if (byTitle !== 0) return byTitle;
      const aPrimary = primaryMappingKeys.has(rowKey(a)) ? 0 : 1;
      const bPrimary = primaryMappingKeys.has(rowKey(b)) ? 0 : 1;
      if (aPrimary !== bPrimary) return aPrimary - bPrimary;
      return supplierLabel(a.api_name).localeCompare(supplierLabel(b.api_name), 'ru');
    });
  }, [listFields, search, supplierFilter, view, primaryMappingKeys]);

  const filteredMergeChoices = useMemo(() => {
    const q = mergeSearch.trim().toLowerCase();
    if (!q) return mergeChoices;
    return mergeChoices.filter(
      (o) => o.title.toLowerCase().includes(q) || o.code.toLowerCase().includes(q),
    );
  }, [mergeChoices, mergeSearch]);

  const supplierOptions = useMemo(() => {
    const apis = [...new Set(listFields.map((f) => f.api_name))].sort();
    return apis.map((api) => ({ value: api, label: supplierLabel(api) }));
  }, [listFields]);

  const filteredProperties = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter((p) => {
      const inProp = (p.title_ru || '').toLowerCase().includes(q)
        || (p.code || '').toLowerCase().includes(q)
        || (p.unit || '').toLowerCase().includes(q);
      if (inProp) return true;
      return (p.mappings || []).some(
        (m) => (m.source_name || '').toLowerCase().includes(q)
          || resolveSourceTitle(m.api_name, m.source_name, m.source_title_ru).toLowerCase().includes(q)
          || supplierLabel(m.api_name).toLowerCase().includes(q),
      );
    });
  }, [properties, search]);

  const hasRawFields = fields.length + mappedFields.length > 0;
  const propertiesCount = summary?.definitions_count ?? properties.length;
  const isDataView = DATA_VIEWS.has(view);

  const filteredByProperty = useMemo(() => {
    const q = valuesSearch.trim().toLowerCase();
    const items = valuesData?.by_property || [];
    if (!q) return items;
    return items.filter((prop) => {
      if ((prop.title_ru || '').toLowerCase().includes(q)) return true;
      if ((prop.code || '').toLowerCase().includes(q)) return true;
      return (prop.value_groups || []).some(
        (g) => (g.display || '').toLowerCase().includes(q),
      );
    });
  }, [valuesData, valuesSearch]);

  const propertyValuesView = useMemo(() => {
    const valuesByCode = new Map(filteredByProperty.map((p) => [p.code, p]));
    const baseProps = properties.length
      ? properties
      : filteredByProperty.map((p) => ({
        code: p.code,
        title_ru: p.title_ru,
        unit: p.unit,
        mappings: [],
      }));

    const q = valuesSearch.trim().toLowerCase();
    let merged = baseProps.map((prop) => {
      const values = valuesByCode.get(prop.code) || {};
      return {
        ...prop,
        value_groups: values.value_groups || [],
        distinct_count: values.distinct_count || 0,
        empty_count: values.empty_count || 0,
        empty_products: values.empty_products || [],
        raw_inputs: values.raw_inputs || [],
        raw_input_count: values.raw_input_count || 0,
        conflict_products: values.conflict_products || 0,
        duplicate_mappings: values.duplicate_mappings || false,
        mappings_count: values.mappings_count || (prop.mappings || []).length,
      };
    });

    if (q) {
      merged = merged.filter((prop) => {
        if (valuesByCode.has(prop.code)) return true;
        if ((prop.title_ru || '').toLowerCase().includes(q)) return true;
        if ((prop.code || '').toLowerCase().includes(q)) return true;
        return (prop.mappings || []).some(
          (m) => (m.source_name || '').toLowerCase().includes(q)
            || resolveSourceTitle(m.api_name, m.source_name, m.source_title_ru).toLowerCase().includes(q)
            || supplierLabel(m.api_name).toLowerCase().includes(q),
        );
      });
    }

    return merged.sort((a, b) => (a.title_ru || a.code).localeCompare(b.title_ru || b.code, 'ru'));
  }, [properties, filteredByProperty, valuesSearch]);

  const clearToast = () => {
    setMsg('');
    setMsgType('info');
  };

  const flashSavedRow = (key, title, removeFromList, kind = 'create') => {
    setFlashKey(key);
    const toastText = kind === 'merge'
      ? `✓ «${title}» — объединено`
      : `✓ «${title}» — поле создано`;
    showToast(toastText, 'success');
    window.setTimeout(() => {
      removeFromList();
      setFlashKey((current) => (current === key ? null : current));
    }, 1400);
  };

  const publishRow = async (row) => {
    const key = rowKey(row);
    if (savingKey) return;

    setSavingKey(key);
    clearToast();

    const res = await api.characteristics.publishField(cat_id, {
      api_name: row.api_name,
      source_name: row.source_name,
      title_ru: row.source_name,
      sample_values: row.sample_values || [],
      recompute: false,
    });

    setSavingKey(null);

    if (res.error) {
      showToast(res.error, 'error');
      return;
    }

    const title = row.source_name;
    const mapping = {
      attribute_code: res.item?.attribute_code,
      attribute_title: title,
    };

    setMappedFields((prev) => {
      if (prev.some((f) => rowKey(f) === key)) return prev;
      return [...prev, { ...row, mapping, status: 'mapped' }];
    });

    setSummary((prev) =>
      prev ? { ...prev, mappings_count: (prev.mappings_count || 0) + 1 } : prev,
    );
    setNeedsRecompute(true);

    const removeFromList = () => {
      if (view === 'unmapped') {
        setFields((prev) => prev.filter((f) => rowKey(f) !== key));
      } else {
        setFields((prev) =>
          prev.map((f) => (rowKey(f) === key ? { ...f, mapping, status: 'mapped' } : f)),
        );
      }
    };

    flashSavedRow(key, title, removeFromList);
  };

  const unmapRow = async (row) => {
    if (!window.confirm(`Отменить настройку «${row.source_name}»?`)) return;

    const key = rowKey(row);
    setSavingKey(key);
    clearToast();

    const res = await api.characteristics.unmapField(cat_id, {
      api_name: row.api_name,
      source_name: row.source_name,
      recompute: false,
    });

    setSavingKey(null);

    if (res.error) {
      showToast(res.error, 'error');
      return;
    }

    setFields((prev) => prev.filter((f) => rowKey(f) !== key));
    setMappedFields((prev) => prev.filter((f) => rowKey(f) !== key));
    setSummary((prev) =>
      prev ? { ...prev, mappings_count: Math.max(0, (prev.mappings_count || 1) - 1) } : prev,
    );
    setNeedsRecompute(true);
    showToast(`«${row.source_name}» снова не настроено`, 'info');
    if (view === 'mapped') load({ silent: true });
  };

  const confirmMerge = async (row, code, titleOverride) => {
    if (!code || savingKey) return;

    const title = titleOverride || code;
    if (
      !window.confirm(
        `Объединить «${row.source_name}» с «${title}»?\n\nТолько если это одно и то же.`,
      )
    ) {
      return;
    }

    const key = rowKey(row);
    setSavingKey(key);

    const res = await api.characteristics.saveFieldMapping(cat_id, {
      api_name: row.api_name,
      source_name: row.source_name,
      attribute_code: code,
      recompute: false,
    });

    setSavingKey(null);

    if (res.error) {
      showToast(res.error, 'error');
      return;
    }

    setMappedFields((prev) => [
      ...prev.filter((f) => rowKey(f) !== key),
      { ...row, mapping: { attribute_code: code, attribute_title: title }, status: 'mapped' },
    ]);
    setMergeRow(null);
    setMergeSearch('');
    setMergeSelected('');
    setNeedsRecompute(true);

    flashSavedRow(key, `${row.source_name} → ${title}`, () => {
      setFields((prev) => prev.filter((f) => rowKey(f) !== key));
    }, 'merge');
  };

  const openMergeModal = (row) => {
    setMergeRow(row);
    setMergeSearch('');
    setMergeSelected('');
  };

  const closeMergeModal = () => {
    setMergeRow(null);
    setMergeSearch('');
    setMergeSelected('');
  };

  const handleLlmAutoMap = async () => {
    if (llmMapping || savingKey) return;
    if (!window.confirm(
      'AI проанализирует ВСЕ поля поставщиков в категории: сгруппирует, задаст названия и единицы из данных, создаст свойства и привязки.\n\nНа корневом разделе (например всё тепловое) это может занять 30–90 минут, не 2–5. Страницу можно обновлять — задача не пропадёт. Повторно «Автонастроить» не нажимайте.\n\nПродолжить?',
    )) {
      return;
    }

    showToast('AI анализирует все поля категории. На большом разделе это десятки минут…', 'info');

    const res = await api.characteristics.llmAutoMap(cat_id, {
      min_confidence: 'high',
      recompute: false,
    });

    if (res.error) {
      showToast(res.error, 'error');
      return;
    }

    if (res.status === 'running' && res.job_id) {
      sessionStorage.setItem(
        llmJobStorageKey(cat_id),
        JSON.stringify({ job_id: res.job_id }),
      );
      pollLlmJob(res.job_id);
      return;
    }

    if (res.applied != null) {
      finishLlmJob(res);
    }
  };

  const stopRecomputePoll = () => {
    if (recomputePollRef.current) {
      window.clearInterval(recomputePollRef.current);
      recomputePollRef.current = null;
    }
  };

  const finishRecomputeJob = useCallback((result, errorText) => {
    stopRecomputePoll();
    sessionStorage.removeItem(recomputeJobStorageKey(cat_id));
    setRecomputing(false);
    setRecomputeProgress(null);
    setRecomputeStartedAt(null);

    if (errorText) {
      showToast(errorText, 'error');
      return;
    }

    setNeedsRecompute(false);
    showToast(`Готово: обновлено ${result?.recomputed ?? 0} товаров.`, 'success');
    load({ silent: true });
    loadValues();
    setView('values');
  }, [cat_id, load, loadValues]);

  const pollRecomputeJob = useCallback((jobId) => {
    stopRecomputePoll();
    setRecomputing(true);

    const tick = async () => {
      const status = await api.characteristics.getRecomputeStatus(cat_id, { job_id: jobId });
      if (status.error) {
        finishRecomputeJob(null, status.error);
        return;
      }
      if (status.progress) setRecomputeProgress(status.progress);
      if (status.started_at) setRecomputeStartedAt(status.started_at);
      if (status.status === 'running') return;
      if (status.status === 'done') {
        finishRecomputeJob(status.result);
        return;
      }
      if (status.status === 'error') {
        finishRecomputeJob(null, status.error || 'Применение к товарам завершилось с ошибкой');
        return;
      }
      if (status.status === 'idle') {
        finishRecomputeJob(null, 'Задача применения не найдена — возможно, сервер перезапускали');
      }
    };

    tick();
    recomputePollRef.current = window.setInterval(tick, 2500);
  }, [cat_id, finishRecomputeJob]);

  useEffect(() => () => stopRecomputePoll(), []);

  useEffect(() => {
    const resume = async () => {
      const saved = sessionStorage.getItem(recomputeJobStorageKey(cat_id));
      const savedJobId = saved ? JSON.parse(saved).job_id : null;
      const status = await api.characteristics.getRecomputeStatus(
        cat_id,
        savedJobId ? { job_id: savedJobId } : {},
      );
      if (status.error) return;
      if (status.status === 'running') {
        if (status.job_id) {
          sessionStorage.setItem(
            recomputeJobStorageKey(cat_id),
            JSON.stringify({ job_id: status.job_id }),
          );
        }
        showToast('Применение к товарам продолжается…', 'info');
        pollRecomputeJob(status.job_id || savedJobId);
      } else if (status.status === 'done' && status.result && savedJobId) {
        sessionStorage.removeItem(recomputeJobStorageKey(cat_id));
        finishRecomputeJob(status.result);
      } else if (savedJobId && status.status === 'idle') {
        sessionStorage.removeItem(recomputeJobStorageKey(cat_id));
      }
    };
    resume();
  }, [cat_id, pollRecomputeJob, finishRecomputeJob]);

  const handleRecompute = async () => {
    if (recomputing || savingKey) return;

    showToast('Применяем настройки ко всем товарам категории. Страницу можно обновлять.', 'info');

    const res = await api.characteristics.recomputeCategory(cat_id);

    if (res.error) {
      showToast(res.error, 'error');
      return;
    }

    if (res.status === 'running' && res.job_id) {
      sessionStorage.setItem(
        recomputeJobStorageKey(cat_id),
        JSON.stringify({ job_id: res.job_id }),
      );
      pollRecomputeJob(res.job_id);
      return;
    }

    if (res.recomputed != null) {
      finishRecomputeJob(res);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(
      'Сбросить все настройки характеристик этой категории?\n\n'
      + 'Будут удалены свойства, привязки поставщиков и унифицированные значения на товарах. '
      + 'Сырые данные поставщиков не затрагиваются.\n\n'
      + 'После сброса нажмите «Автонастроить (AI)» заново.',
    )) {
      return;
    }

    setResetting(true);
    showToast('Сбрасываем настройки категории…', 'info');

    const res = await api.characteristics.resetCategory(cat_id);

    setResetting(false);

    if (res.error) {
      showToast(res.error, 'error');
      return;
    }

    setNeedsRecompute(false);
    setProperties([]);
    setValuesData(null);
    setView('unmapped');
    showToast(
      `Сброс выполнен: свойств ${res.definitions_deactivated || 0}, `
      + `привязок ${res.mappings_archived || 0}, товаров ${res.products_cleared || 0}.`,
      'success',
    );
    load({ silent: true });
  };

  const stopBxPoll = () => {
    if (bxPollRef.current) {
      window.clearInterval(bxPollRef.current);
      bxPollRef.current = null;
    }
  };

  const finishBxJob = useCallback((result, errorText) => {
    stopBxPoll();
    sessionStorage.removeItem(bxJobStorageKey(cat_id));
    setBxExporting(false);
    setBxProgress(null);

    if (errorText) {
      showToast(errorText, 'error');
      setBxResult(null);
      return;
    }

    setBxResult(result || null);
    showToast(
      `Bitrix: отправлено ${result?.sent ?? 0} товаров, записано ${result?.written ?? 0}`
      + `${result?.failed ? `, ошибок ${result.failed}` : ''}`
      + `${result?.dry_run ? ' (проверка без записи)' : ''}.`,
      result?.failed ? 'error' : 'success',
    );
    loadProperties();
  }, [cat_id, loadProperties]);

  const pollBxJob = useCallback((jobId) => {
    stopBxPoll();
    setBxExporting(true);

    const tick = async () => {
      const status = await api.characteristics.getExportBitrixStatus(cat_id, { job_id: jobId });
      if (status.error) {
        finishBxJob(null, status.error);
        return;
      }
      if (status.progress) setBxProgress(status.progress);
      if (status.status === 'running') return;
      if (status.status === 'done') {
        finishBxJob(status.result);
        return;
      }
      if (status.status === 'error') {
        finishBxJob(null, status.error || 'Выгрузка в Bitrix завершилась с ошибкой');
        return;
      }
      if (status.status === 'idle') {
        finishBxJob(null, 'Задача выгрузки не найдена — возможно, сервер перезапускали');
      }
    };

    tick();
    bxPollRef.current = window.setInterval(tick, 2500);
  }, [cat_id, finishBxJob]);

  useEffect(() => () => stopBxPoll(), []);

  useEffect(() => {
    const resume = async () => {
      const saved = sessionStorage.getItem(bxJobStorageKey(cat_id));
      const savedJobId = saved ? JSON.parse(saved).job_id : null;
      if (!savedJobId) return;
      const status = await api.characteristics.getExportBitrixStatus(cat_id, { job_id: savedJobId });
      if (status.error) return;
      if (status.status === 'running') {
        showToast('Выгрузка в Bitrix продолжается…', 'info');
        pollBxJob(savedJobId);
      } else if (status.status === 'done' && status.result) {
        sessionStorage.removeItem(bxJobStorageKey(cat_id));
        finishBxJob(status.result);
      } else if (status.status === 'idle') {
        sessionStorage.removeItem(bxJobStorageKey(cat_id));
      }
    };
    resume();
  }, [cat_id, pollBxJob, finishBxJob]);

  const handleExportBitrix = async (dryRun) => {
    if (!dryRun && !window.confirm(
      'Выгрузить характеристики в Bitrix для всех товаров категории с bxid?\n\n'
      + 'У каждого товара будут удалены значения старых («мусорных») свойств '
      + '(кроме технических с сортировкой ≥ 11000 — Артикул, Код 1С, цены и т.п.), '
      + 'а затем записаны новые унифицированные значения.\n\n'
      + 'Рекомендуется сначала сделать «Проверить (без записи)».',
    )) {
      return;
    }

    showToast(
      dryRun ? 'Проверяем выгрузку в Bitrix — без записи…' : 'Выгружаем характеристики в Bitrix…',
      'info',
    );

    const res = await api.characteristics.exportCategoryToBitrix(cat_id, { dry_run: dryRun });

    if (res.error) {
      showToast(res.error, 'error');
      return;
    }

    if (res.status === 'running' && res.job_id) {
      sessionStorage.setItem(bxJobStorageKey(cat_id), JSON.stringify({ job_id: res.job_id }));
      pollBxJob(res.job_id);
      return;
    }

    if (res.result) {
      finishBxJob(res.result);
    }
  };

  if (loading && !summary) return <Loader />;

  const needsAttention = summary?.needs_attention || 0;
  const ready = summary?.ready_for_export || 0;

  return (
    <>
      <Header
        title={summary?.category_name || 'Категория'}
        breadCrumbs={[
          { name: 'Характеристики', link: '/characteristics' },
          { name: summary?.category_name || cat_id, link: '' },
        ]}
      />

      <div className={s.wrap}>
      <p className={s.lead}>
        <strong>AI:</strong> «Автонастроить» — склеивает поля поставщиков и сразу сводит значения
        одного смысла (Сенсорная/Сенсорный, Электрический/электричество).{' '}
        <strong>Вручную:</strong> «Создать поле» / «Объединить…» — только исключения.{' '}
        <strong>Финал:</strong> «Применить к товарам» — записать уже готовые значения на карточки.{' '}
        <strong>Бренд</strong>, <strong>артикул</strong> и <strong>склады</strong> (Самара, Казань…) на сайт не выводятся.
      </p>

      {needsRecompute && (
        <p className={s.warnBanner}>
          Есть новые настройки, ещё не применённые к товарам. Нажмите «Применить к товарам».
        </p>
      )}

      {llmMapping && (
        <p className={s.llmBanner}>
          AI настраивает категорию
          {llmStartedAt ? ` — уже ${formatElapsed(llmStartedAt, llmClock || Date.now())}` : ''}.
          {' '}На большом разделе это обычно 30–90 минут, не 2–5.
          {' '}Страницу можно обновлять — задача не пропадёт. Повторно «Автонастроить» не нажимайте.
        </p>
      )}

      {recomputing && (
        <p className={s.llmBanner}>
          Применяем настройки к товарам
          {recomputeProgress?.total
            ? ` — ${recomputeProgress.done} из ${recomputeProgress.total}`
            : recomputeStartedAt
              ? ` — уже ${formatElapsed(recomputeStartedAt, llmClock || Date.now())}`
              : '…'}
          . Страницу можно обновлять — задача не пропадёт. Повторно «Применить» не нажимайте.
        </p>
      )}

      {bxExporting && (
        <p className={s.llmBanner}>
          Выгружаем в Bitrix{bxProgress?.total ? ` — ${bxProgress.done} из ${bxProgress.total} товаров` : '…'}.
          Можно обновить страницу, прогресс сохранится.
        </p>
      )}

      {!bxExporting && bxResult && (
        <p className={bxResult.failed ? s.warnBanner : s.llmBanner}>
          {bxResult.dry_run ? 'Проверка Bitrix (без записи): ' : 'Выгрузка в Bitrix: '}
          отправлено {bxResult.sent} из {bxResult.product_total} товаров, записано {bxResult.written},
          {' '}очищено старых значений {bxResult.cleared_total}
          {bxResult.failed ? `, ошибок ${bxResult.failed}` : ''}
          {bxResult.skipped_no_values ? `, без значений ${bxResult.skipped_no_values}` : ''}.
        </p>
      )}

      {summary && (
        <div className={s.cards}>
          <div className={`${s.card} ${s.cardGreen}`}>
            <div className={s.cardNum}>{ready}</div>
            <div className={s.cardLabel}>товаров с данными</div>
          </div>
          <div className={`${s.card} ${needsAttention ? s.cardYellow : s.cardGrey}`}>
            <div className={s.cardNum}>{needsAttention}</div>
            <div className={s.cardLabel}>расхождения</div>
          </div>
          <div className={s.card}>
            <div className={s.cardNum}>{summary.mappings_count || 0}</div>
            <div className={s.cardLabel}>привязок поставщиков</div>
          </div>
          <div className={s.card}>
            <div className={s.cardNum}>{propertiesCount}</div>
            <div className={s.cardLabel}>наших свойств</div>
          </div>
        </div>
      )}

      <div className={s.actionsBar}>
        {hasRawFields && (
          <button
            className="btn dark"
            disabled={llmMapping || recomputing}
            onClick={handleLlmAutoMap}
          >
            {llmMapping ? 'AI настраивает…' : 'Автонастроить (AI)'}
          </button>
        )}
        <button
          type="button"
          className={view === 'properties' ? 'btn dark' : 'btn grey'}
          onClick={() => setView('properties')}
        >
          Все свойства{propertiesCount ? ` (${propertiesCount})` : ''}
        </button>
        <button
          className={needsRecompute ? 'btn dark' : 'btn grey'}
          disabled={recomputing}
          onClick={handleRecompute}
        >
          {recomputing ? 'Применяем…' : 'Применить к товарам'}
        </button>
        {needsAttention > 0 && (
          <button className="btn grey" onClick={() => navigate(`/characteristics/categories/${cat_id}/exceptions`)}>
            Расхождения ({needsAttention})
          </button>
        )}
        <button
          type="button"
          className="btn grey"
          disabled={resetting || llmMapping || recomputing}
          onClick={handleReset}
        >
          {resetting ? 'Сброс…' : 'Сбросить настройки'}
        </button>
        <button
          type="button"
          className="btn grey"
          disabled={bxExporting || !propertiesCount}
          onClick={() => handleExportBitrix(true)}
        >
          {bxExporting ? 'Bitrix…' : 'Проверить Bitrix (без записи)'}
        </button>
        <button
          type="button"
          className="btn dark"
          disabled={bxExporting || !propertiesCount}
          onClick={() => handleExportBitrix(false)}
        >
          {bxExporting ? 'Выгружаем…' : 'Выгрузить в Bitrix'}
        </button>
      </div>

      {msg && (
        <div
          className={`${s.toast} ${msgType === 'success' ? s.toastSuccess : ''} ${msgType === 'error' ? s.toastError : ''}`}
          role="status"
        >
          {msg}
        </div>
      )}

      <div className={s.panel}>
        <div className={s.panelHead}>
          <div className={s.tabs}>
            <button className={view === 'unmapped' ? s.tabActive : s.tab} onClick={() => setView('unmapped')}>
              Не настроено {fields.length > 0 ? `(${fields.length})` : ''}
            </button>
            <button type="button" className={view === 'mapped' ? s.tabActive : s.tab} onClick={() => setView('mapped')}>
              Настроено {mappedFields.length > 0 ? `(${mappedFields.length})` : ''}
            </button>
            <button type="button" className={view === 'properties' ? s.tabActive : s.tab} onClick={() => setView('properties')}>
              Свойства {propertiesCount > 0 ? `(${propertiesCount})` : ''}
            </button>
            <button type="button" className={view === 'values' ? s.tabActive : s.tab} onClick={() => setView('values')}>
              Значения
            </button>
          </div>
          {!isDataView && listFields.length > 0 && (
            <div className={s.filterBar}>
              <input
                type="search"
                className={s.searchInput}
                placeholder={view === 'mapped' ? 'Поиск по полю или поставщику…' : 'Поиск по названию поля…'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {supplierOptions.length > 1 && (
                <select
                  className={s.supplierSelect}
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                >
                  <option value="">Все поставщики</option>
                  {supplierOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}
              {(search || supplierFilter) && (
                <span className={s.filterHint}>
                  {filteredFields.length} из {listFields.length}
                </span>
              )}
            </div>
          )}
          {view === 'properties' && (
            <div className={s.filterBar}>
              <input
                type="search"
                className={s.searchInput}
                placeholder="Поиск по свойству или поставщику…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
          {view === 'values' && (
            <div className={s.filterBar}>
              <input
                type="search"
                className={s.searchInput}
                placeholder="Поиск по свойству или значению…"
                value={valuesSearch}
                onChange={(e) => setValuesSearch(e.target.value)}
              />
            </div>
          )}
        </div>

      {view === 'values' && (
        <p className={s.mappedHint}>
          {valuesData?.is_preview !== false ? (
            <>Предпросмотр: те значения, которые AI уже свёл. «Применить» только запишет их на товары.</>
          ) : (
            <>Сохранённые значения на товарах.</>
          )}
          {valuesData && (
            <>
              {' '}
              Товаров в категории: {valuesData.product_total}
              {valuesData.with_preview != null ? `, с данными: ${valuesData.with_preview}` : ''}.
            </>
          )}
        </p>
      )}

      {view === 'properties' && (
        <p className={s.mappedHint}>
          Канонические свойства категории: название, единица хранения, привязки поставщиков и transform для унификации значений.
        </p>
      )}

      {view === 'mapped' && mappedFields.length > 0 && (
        <p className={s.mappedHint}>
          Одно «наше поле» может иметь несколько строк — по одной на каждого поставщика.
          <strong> Основное</strong> — создано первым, <strong>Объединено</strong> — привязано позже.
        </p>
      )}

      {(loading || (view === 'properties' && propertiesLoading) || (view === 'values' && valuesLoading)) && <Loader />}

      {view === 'properties' && !propertiesLoading && filteredProperties.length === 0 && (
        <p className={s.empty}>
          {properties.length === 0
            ? 'Свойств пока нет — запустите «Автонастроить (AI)».'
            : 'По фильтру ничего не найдено.'}
        </p>
      )}

      {view === 'values' && valuesLoading && (
        <p className={s.infoMsg}>Считаем значения по всем товарам категории — может занять до минуты…</p>
      )}

      {view === 'values' && !valuesLoading && propertiesCount === 0 && (
        <p className={s.empty}>
          Сначала настройте свойства — «Автонастроить (AI)» или вручную.
        </p>
      )}

      {view === 'values' && !valuesLoading && propertiesCount > 0 && propertyValuesView.length === 0 && (
        <p className={s.empty}>По фильтру ничего не найдено.</p>
      )}

      {view === 'values' && !valuesLoading && propertyValuesView.length > 0 && (
        <div className={s.propValuesList}>
          {propertyValuesView.map((prop) => {
            const groups = prop.value_groups || [];
            const hasValues = groups.length > 0;
            const emptyCount = prop.empty_count || 0;

            return (
              <article key={prop.code} className={s.propValueCard}>
                <header className={s.propValueHead}>
                  <div>
                    <h3 className={s.propValueTitle}>
                      {prop.title_ru}
                      {prop.unit ? `, ${formatUnit(prop.unit)}` : ''}
                    </h3>
                    <span className={s.grey}>{prop.code}</span>
                  </div>
                  <div className={s.propValueStats}>
                    {hasValues && (
                      <span>{prop.distinct_count || groups.length} на сайте</span>
                    )}
                    {prop.raw_input_count > 0 && (
                      <span>{prop.raw_input_count} у поставщ.</span>
                    )}
                    {prop.conflict_products > 0 && (
                      <span className={`${s.red} ${s.boldStat}`}>конфликт: {prop.conflict_products}</span>
                    )}
                    {emptyCount > 0 && (
                      <span className={s.yellow}>пусто: {emptyCount}</span>
                    )}
                  </div>
                </header>

                {(prop.duplicate_mappings || prop.conflict_products > 0) && (
                  <div className={s.propValueWarn}>
                    {prop.duplicate_mappings && (
                      <span>Дубли привязок — одно поле поставщика привязано дважды.</span>
                    )}
                    {prop.conflict_products > 0 && (
                      <span>
                        {prop.conflict_products} товар(ов): разные поставщики дали разные значения —
                        победитель по приоритету привязки.
                      </span>
                    )}
                  </div>
                )}

                {prop.raw_input_count > 0 && (
                  <div className={s.propValueSection}>
                    <p className={s.propValueLabel}>
                      Сырые данные у поставщиков ({prop.raw_input_count} вариантов)
                    </p>
                    <p className={s.propValueHint}>
                      Что реально приходит в полях до унификации. Из них получаются значения на сайте.
                    </p>
                    <ul className={s.rawInputsList}>
                      {(prop.raw_inputs || []).map((src) => (
                        <li key={`${src.api_name}-${src.source_name}-${src.raw_value}`}>
                          {formatSourceLine(src)}
                        </li>
                      ))}
                      {prop.raw_input_count > (prop.raw_inputs || []).length && (
                        <li className={s.grey}>
                          …ещё {prop.raw_input_count - (prop.raw_inputs || []).length} вариантов
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                <div className={s.propValueSection}>
                  <p className={s.propValueLabel}>Привязки поставщиков</p>
                  {(prop.mappings || []).length === 0 ? (
                    <p className={s.grey}>Не привязано — добавьте в «Не настроено» или через AI.</p>
                  ) : (
                    <ul className={s.propMappingsInline}>
                      {(prop.mappings || []).map((m) => (
                        <li key={`${m.api_name}-${m.source_name}`}>
                          <span className={s.supplierBadge}>{supplierLabel(m.api_name)}</span>
                          <span className={s.sourceName}>
                            {formatSupplierField(m.api_name, m.source_name, m.source_title_ru)}
                          </span>
                          {m.value_transform && m.value_transform !== 'as_is' && (
                            <span className={s.transformTag}> → {m.value_transform}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className={s.propValueSection}>
                  <p className={s.propValueLabel}>Значения на сайте</p>
                  {!hasValues && emptyCount === 0 && (
                    <p className={s.grey}>Пока нет данных у товаров.</p>
                  )}
                  {(hasValues || emptyCount > 0) && (
                    <table className={s.propValueTable}>
                      <thead>
                        <tr>
                          <th>На сайте</th>
                          <th>Товаров</th>
                          <th>На основе чего</th>
                          <th>Примеры</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groups.map((g) => (
                          <tr
                            key={`${prop.code}-${g.display}`}
                            className={g.conflict_count > 0 ? s.rowConflict : ''}
                          >
                            <td>
                              <strong>{g.display}</strong>
                              {g.conflict_count > 0 && (
                                <span className={s.conflictTag}>
                                  {' '}конфликт {g.conflict_count}
                                </span>
                              )}
                            </td>
                            <td className={s.colCount}>{g.product_count}</td>
                            <td>
                              {(g.source_breakdown || []).length === 0 ? (
                                <span className={s.grey}>—</span>
                              ) : (
                                <ul className={s.sourceBreakdown}>
                                  {(g.source_breakdown || []).map((src) => (
                                    <li key={`${src.api_name}-${src.source_name}-${src.raw_value}`}>
                                      {formatSourceLine(src)}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </td>
                            <td>
                              {(g.products || []).length === 0 ? (
                                <span className={s.grey}>—</span>
                              ) : (
                                <ul className={s.productLinks}>
                                  {(g.products || []).slice(0, 5).map((p) => (
                                    <li key={p.good_id}>
                                      <button
                                        type="button"
                                        className={s.linkBtn}
                                        onClick={() => navigate(`/characteristics/products/${p.good_id}`)}
                                      >
                                        {p.title}
                                      </button>
                                    </li>
                                  ))}
                                  {(g.products || []).length > 5 && (
                                    <li className={s.grey}>…ещё {(g.products || []).length - 5}</li>
                                  )}
                                </ul>
                              )}
                            </td>
                          </tr>
                        ))}
                        {emptyCount > 0 && (
                          <tr>
                            <td><span className={s.grey}>— нет значения —</span></td>
                            <td className={s.colCount}>{emptyCount}</td>
                            <td>
                              <ul className={s.productLinks}>
                                {(prop.empty_products || []).slice(0, 5).map((p) => (
                                  <li key={p.good_id}>
                                    <button
                                      type="button"
                                      className={s.linkBtn}
                                      onClick={() => navigate(`/characteristics/products/${p.good_id}`)}
                                    >
                                      {p.title}
                                    </button>
                                  </li>
                                ))}
                                {(prop.empty_products || []).length > 5 && (
                                  <li className={s.grey}>
                                    …ещё {(prop.empty_products || []).length - 5}
                                  </li>
                                )}
                              </ul>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {view !== 'properties' && view !== 'values' && !loading && listFields.length === 0 && (
        <p className={s.empty}>
          {view === 'unmapped' ? 'Все поля настроены.' : 'Пока ничего не настроено.'}
        </p>
      )}

      {view === 'properties' && filteredProperties.length > 0 && (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Свойство</th>
                <th>Код</th>
                <th>Единица</th>
                <th>Тип</th>
                <th>Привязки</th>
                <th>Bitrix</th>
              </tr>
            </thead>
            <tbody>
              {filteredProperties.map((prop) => (
                <tr key={prop.code}>
                  <td className={s.fieldTitleCell}><strong>{prop.title_ru}</strong></td>
                  <td className={s.samples}>{prop.code}</td>
                  <td>{formatUnit(prop.unit)}</td>
                  <td className={s.samples}>{prop.value_type || '—'}</td>
                  <td>
                    {(prop.mappings || []).length === 0 ? (
                      <span className={s.grey}>—</span>
                    ) : (
                      <ul className={s.propMappings}>
                        {(prop.mappings || []).map((m) => (
                          <li key={`${m.api_name}-${m.source_name}`}>
                            <span className={s.supplierBadge}>{supplierLabel(m.api_name)}</span>
                            {' '}
                            <span className={s.sourceName}>
                              {formatSupplierField(m.api_name, m.source_name, m.source_title_ru)}
                            </span>
                            <span className={s.transformTag}> → {m.value_transform || 'as_is'}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className={s.bxCell}>
                    <div className={s.bxId}>
                      {prop.bx_property_id ? (
                        <span className={s.grey}>ID {prop.bx_property_id}</span>
                      ) : (
                        <span className={s.grey}>не выгружено</span>
                      )}
                    </div>
                    <label className={s.bxCheckbox}>
                      <input
                        type="checkbox"
                        checked={!!prop.bx_show_on_list}
                        onChange={(e) => handleBxFlagChange(prop, 'bx_show_on_list', e.target.checked)}
                      />
                      список
                    </label>
                    <label className={s.bxCheckbox}>
                      <input
                        type="checkbox"
                        checked={!!prop.bx_show_on_detail}
                        onChange={(e) => handleBxFlagChange(prop, 'bx_show_on_detail', e.target.checked)}
                      />
                      детальная
                    </label>
                    <label className={s.bxCheckbox}>
                      <input
                        type="checkbox"
                        checked={!!prop.bx_show_in_filter}
                        onChange={(e) => handleBxFlagChange(prop, 'bx_show_in_filter', e.target.checked)}
                      />
                      фильтр
                      {!prop.bx_filter_manual && <span className={s.grey}> (авто)</span>}
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view !== 'properties' && view !== 'values' && listFields.length > 0 && (
        <div className={s.tableWrap}>
        <table className={`${s.table} ${view === 'mapped' ? s.tableMapped : ''}`}>
          <thead>
            <tr>
              {view === 'mapped' && <th>Наше поле</th>}
              {view === 'mapped' && <th>Связь</th>}
              <th>Поставщик</th>
              <th>Как у поставщика</th>
              <th>Товаров</th>
              <th>Примеры</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredFields.map((row, index) => {
              const mapped = row.mapping;
              const key = rowKey(row);
              const isSaving = savingKey === key;
              const isFlashing = flashKey === key;
              const isPrimary = primaryMappingKeys.has(key);
              const prevRow = index > 0 ? filteredFields[index - 1] : null;
              const showFieldTitle = view !== 'mapped'
                || !prevRow
                || prevRow.mapping?.attribute_code !== mapped?.attribute_code;

              return (
                <tr
                  key={key}
                  className={`${mapped ? s.rowMapped : s.rowUnmapped} ${isSaving ? s.rowSaving : ''} ${isFlashing ? s.rowFlash : ''} ${view === 'mapped' && !isPrimary ? s.rowLinked : ''}`}
                >
                  {view === 'mapped' && (
                    <td className={showFieldTitle ? s.fieldTitleCell : s.fieldTitleCellEmpty}>
                      {showFieldTitle ? (
                        <strong>{mapped?.attribute_title}</strong>
                      ) : (
                        <span className={s.fieldTitleContinued}>↳</span>
                      )}
                    </td>
                  )}
                  {view === 'mapped' && (
                    <td>
                      {isFlashing ? (
                        <span className={s.green}>✓</span>
                      ) : isPrimary ? (
                        <span className={s.linkBadgePrimary}>Основное</span>
                      ) : (
                        <span className={s.linkBadgeLinked}>Объединено</span>
                      )}
                    </td>
                  )}
                  <td className={s.colSupplier}>
                    <span className={s.supplierBadge}>{supplierLabel(row.api_name)}</span>
                  </td>
                  <td className={s.sourceName}>
                    {formatSupplierField(row.api_name, row.source_name, row.source_title_ru)}
                  </td>
                  <td className={s.colCount}>{row.product_count}</td>
                  <td className={s.samples} title={(row.sample_values || []).slice(0, 3).join(', ')}>
                    {(row.sample_values || []).slice(0, 3).join(', ')}
                  </td>
                  <td className={`${s.rowActions} ${s.colActions}`}>
                    {isFlashing && <span className={s.green}>✓ Готово</span>}
                    {!mapped && !isSaving && !isFlashing && (
                      <div className={s.rowActionsInner}>
                        <button className="btn small dark" onClick={() => publishRow(row)}>
                          Создать поле
                        </button>
                        {mergeChoices.length > 0 && (
                          <button
                            type="button"
                            className={s.linkBtn}
                            onClick={() => openMergeModal(row)}
                          >
                            Объединить…
                          </button>
                        )}
                      </div>
                    )}
                    {isSaving && <span className={s.yellow}>Сохраняю…</span>}
                    {mapped && !isSaving && (
                      <button className="btn grey small" onClick={() => unmapRow(row)}>
                        Отменить
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

          {view !== 'properties' && view !== 'values' && filteredFields.length === 0 && listFields.length > 0 && (
            <p className={s.empty}>По фильтру ничего не найдено.</p>
          )}
      </div>
      </div>

      {mergeRow && (
        <div className={s.modalOverlay} onClick={closeMergeModal}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className={s.modalTitle}>Объединить с существующим полем</h3>
            <p className={s.modalHint}>
              «{mergeRow.source_name}» ({supplierLabel(mergeRow.api_name)}) — только если это то же поле,
              что уже настроено.
            </p>
            <input
              type="search"
              className={s.searchInput}
              placeholder="Поиск…"
              value={mergeSearch}
              onChange={(e) => setMergeSearch(e.target.value)}
              autoFocus
            />
            <ul className={s.mergeList}>
              {filteredMergeChoices.map((o) => (
                <li key={o.code}>
                  <button
                    type="button"
                    className={mergeSelected === o.code ? s.mergeOptionActive : s.mergeOption}
                    onClick={() => setMergeSelected(o.code)}
                  >
                    {o.title}
                  </button>
                </li>
              ))}
            </ul>
            {filteredMergeChoices.length === 0 && (
              <p className={s.empty}>Ничего не найдено.</p>
            )}
            <div className={s.modalActions}>
              <button
                className="btn"
                disabled={!mergeSelected || savingKey === rowKey(mergeRow)}
                onClick={() => confirmMerge(
                  mergeRow,
                  mergeSelected,
                  mergeChoices.find((o) => o.code === mergeSelected)?.title,
                )}
              >
                Объединить
              </button>
              <button type="button" className="btn grey" onClick={closeMergeModal}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoryCharacteristics;
